/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Formula, isFormulaEmpty, UomQuantityMapper } from '@gh/builder-shared-data';
import {
  createGroupArrayIndexDriver,
  createGroupIndexDriver,
  createOrderIndexDriver,
  createTableDriver,
  isEntityInvalid,
  newEntityComparator,
  queryFormMode,
  queryFormModeCreate,
  setEntityFields,
  setNewDirtyType,
  Table,
  whenDirty,
  whenExisting,
  whenModified,
} from '@gh/common-shared';
import { EMPTY_ID, ID, WithDirty, WithFormMode, WithRecalculationNeeded } from '@gh/common-shared-data';
import { FormMode, isEditableFormMode } from '@gh/core-ui';
import { FocusedCell } from '@gh/core-ui-grid';
import { composeComparators, valueComparator } from '@gh/core-util';

import { find, first, get, isEmpty, isNil, keyBy, omit, overEvery, sortBy } from 'lodash';

import {
  getHpeComponentId,
  getHpeCostCenterId,
  getHpeCuttingListItemId,
  getHpeItemId,
  getHpeTopId,
  getHpeTopRefItemId,
  getHpeUomRefItemId,
  HpeComponent,
  HpeCostCenter,
  HpeCuttingListItem,
  HpeDetails,
  HpeDetailsGetComponents,
  HpeDetailsGetCostCenter,
  HpeDetailsGetCostCenterRequest,
  HpeDetailsSave,
  HpeElement,
  HpeElementType,
  HpeItem,
  HpeItemType,
  HpeTop,
  HpeTopRefItem,
  HpeTopViewUsage,
  HpeUomRefItem,
  isHpeContractPriceTop,
  isHpeElementHasInvalidFormula,
  isHpeFreeItem,
  isHpeItemFromComponent,
} from '../../resources/hpe-details.model';

export enum HpeDetailsStateEventType {
  Load, LoadWithCostCenter, Merge,
  CostCenterAdd, CostCenterRemove, CostCenterSelect, CostCenterLoad, CostCenterSetField,
  TopSetField, TopRemove, TopCommit, TopReset,
  ComponentAddSet, ComponentSetField, ComponentRemove,
  CuttingListItemAdd, CuttingListItemSetField, CuttingListItemRemove,
  CostCenterItemAddSet, CostCenterItemSetField, CostCenterItemRemove,
  ComponentSelect, CostCenterItemSelect, DetailsTabSelect,
  UomAddSet, TopAddSet,
  NavigateToInvalid, NavigateToInvalidTop, HighlightInvalid, ResetFocusedCell,
  ResetFormulaNeedPropagation,
  InitHistory, SaveViewToHistory, AddToHistory, Undo, Redo,
}

export type HpeDetailsStateLoadEvent = { type: HpeDetailsStateEventType.Load; details: HpeDetails };
export type HpeDetailsStateLoadWithCostCenterEvent = { type: HpeDetailsStateEventType.LoadWithCostCenter; details: HpeDetails; costCenterId?: ID; costCenter?: HpeDetailsGetCostCenter };
export type HpeDetailsStateMergeEvent = { type: HpeDetailsStateEventType.Merge; details: HpeDetails };

export type HpeDetailsStateUomAddSet = { type: HpeDetailsStateEventType.UomAddSet; uomSet: HpeUomRefItem[] };
export type HpeDetailsStateTopAddSet = { type: HpeDetailsStateEventType.TopAddSet; topSet: HpeTopRefItem[] };
export type HpeDetailsStateCostCenterAddEvent = { type: HpeDetailsStateEventType.CostCenterAdd; costCenter: HpeCostCenter };
export type HpeDetailsStateCostCenterSetFieldEvent = { type: HpeDetailsStateEventType.CostCenterSetField; costCenterId: ID; name: string; value: any };
export type HpeDetailsStateCostCenterRemoveEvent = { type: HpeDetailsStateEventType.CostCenterRemove; costCenterId: ID };
export type HpeDetailsStateCostCenterLoadEvent = { type: HpeDetailsStateEventType.CostCenterLoad; costCenterId: ID; costCenter: HpeDetailsGetCostCenter };

export type HpeDetailsStateTopRemoveEvent = { type: HpeDetailsStateEventType.TopRemove; topId: ID };
export type HpeDetailsStateTopSetFieldEvent = { type: HpeDetailsStateEventType.TopSetField; topId: ID; name: string; value: any };
export type HpeDetailsStateTopCommitEvent = { type: HpeDetailsStateEventType.TopCommit; topId: ID };
export type HpeDetailsStateTopResetEvent = { type: HpeDetailsStateEventType.TopReset; top: HpeTop };

export type HpeDetailsStateComponentAddSetEvent = { type: HpeDetailsStateEventType.ComponentAddSet; componentSet: HpeDetailsGetComponents };
export type HpeDetailsStateComponentSetFieldEvent = { type: HpeDetailsStateEventType.ComponentSetField; componentId: ID; name: string; value: any };
export type HpeDetailsStateComponentRemoveEvent = { type: HpeDetailsStateEventType.ComponentRemove; componentId: ID };

export type HpeDetailsStateCostCenterItemAddSetEvent = { type: HpeDetailsStateEventType.CostCenterItemAddSet; costCenterId: ID; itemSet: HpeItem[] };
export type HpeDetailsStateCostCenterItemSetFieldEvent = { type: HpeDetailsStateEventType.CostCenterItemSetField; itemId: ID; name: string; value: any };
export type HpeDetailsStateCostCenterItemRemoveEvent = { type: HpeDetailsStateEventType.CostCenterItemRemove; itemId: ID };

export type HpeDetailsStateCostCenterSelectEvent = { type: HpeDetailsStateEventType.CostCenterSelect; costCenterId?: ID };
export type HpeDetailsStateComponentSelectEvent = { type: HpeDetailsStateEventType.ComponentSelect; componentId?: ID };
export type HpeDetailsStateItemSelectEvent = { type: HpeDetailsStateEventType.CostCenterItemSelect; itemId?: ID };
export type HpeDetailsStateDetailsTabSelectEvent = { type: HpeDetailsStateEventType.DetailsTabSelect; name: string };

export type HpeDetailsStateDetailsNavigateToInvalidEvent = { type: HpeDetailsStateEventType.NavigateToInvalid };
export type HpeDetailsStateDetailsNavigateToInvalidTopEvent = { type: HpeDetailsStateEventType.NavigateToInvalidTop; topId: ID };
export type HpeDetailsStateDetailsHighlightInvalidEvent = { type: HpeDetailsStateEventType.HighlightInvalid };
export type HpeDetailsStateDetailsResetFocusedCellEvent = { type: HpeDetailsStateEventType.ResetFocusedCell };

export type HpeDetailsStateInitHistoryEvent = { type: HpeDetailsStateEventType.InitHistory };
export type JeDetailsStateSaveViewToHistoryEvent = { type: HpeDetailsStateEventType.SaveViewToHistory };
export type HpeDetailsStateAddToHistoryEvent = { type: HpeDetailsStateEventType.AddToHistory };
export type HpeDetailsStateUndoEvent = { type: HpeDetailsStateEventType.Undo };
export type HpeDetailsStateRedoEvent = { type: HpeDetailsStateEventType.Redo };

export type HpeDetailsStateCuttingListItemAddEvent = {
  type: HpeDetailsStateEventType.CuttingListItemAdd;
  sourceType: HpeElementType;
  sourceId: ID;
  item: HpeCuttingListItem;
};

export type HpeDetailsStateCuttingListItemRemoveEvent = {
  type: HpeDetailsStateEventType.CuttingListItemRemove;
  sourceType: HpeElementType;
  sourceId: ID;
  cuttingId: ID;
};

export type HpeDetailsStateCuttingListItemSetFieldEvent = {
  type: HpeDetailsStateEventType.CuttingListItemSetField;
  sourceType: HpeElementType;
  sourceId: ID;
  cuttingId: ID;
  name: string;
  value: any;
};

export type HpeDetailsStateResetFormulaNeedPropagationEvent = {
  type: HpeDetailsStateEventType.ResetFormulaNeedPropagation;
  sourceType: HpeElementType;
  sourceId: ID;
};

export enum HpeComponentDetailsTab {
  Items = 'ITEMS',
  CuttingList = 'CUTTING_LIST',
  Formula = 'FORMULA',
  Memo = 'MEMO',
}

export enum HpeItemDetailsTab {
  Component = 'COMPONENT',
  CuttingList = 'CUTTING_LIST',
  Formula = 'FORMULA',
  Memo = 'MEMO',
}

export type HpeDetailsStateEvent =
  HpeDetailsStateLoadEvent
  | HpeDetailsStateLoadWithCostCenterEvent
  | HpeDetailsStateMergeEvent
  | HpeDetailsStateUomAddSet
  | HpeDetailsStateTopAddSet
  | HpeDetailsStateCostCenterAddEvent
  | HpeDetailsStateCostCenterRemoveEvent
  | HpeDetailsStateCostCenterLoadEvent
  | HpeDetailsStateCostCenterSetFieldEvent
  | HpeDetailsStateTopRemoveEvent
  | HpeDetailsStateTopSetFieldEvent
  | HpeDetailsStateTopCommitEvent
  | HpeDetailsStateTopResetEvent
  | HpeDetailsStateComponentAddSetEvent
  | HpeDetailsStateComponentSetFieldEvent
  | HpeDetailsStateComponentRemoveEvent
  | HpeDetailsStateCuttingListItemAddEvent
  | HpeDetailsStateCuttingListItemRemoveEvent
  | HpeDetailsStateCuttingListItemSetFieldEvent
  | HpeDetailsStateCostCenterItemAddSetEvent
  | HpeDetailsStateCostCenterItemSetFieldEvent
  | HpeDetailsStateCostCenterItemRemoveEvent
  | HpeDetailsStateCostCenterSelectEvent
  | HpeDetailsStateComponentSelectEvent
  | HpeDetailsStateItemSelectEvent
  | HpeDetailsStateDetailsTabSelectEvent
  | HpeDetailsStateDetailsNavigateToInvalidEvent
  | HpeDetailsStateDetailsNavigateToInvalidTopEvent
  | HpeDetailsStateDetailsHighlightInvalidEvent
  | HpeDetailsStateDetailsResetFocusedCellEvent
  | HpeDetailsStateResetFormulaNeedPropagationEvent
  | HpeDetailsStateInitHistoryEvent
  | JeDetailsStateSaveViewToHistoryEvent
  | HpeDetailsStateAddToHistoryEvent
  | HpeDetailsStateUndoEvent
  | HpeDetailsStateRedoEvent;

export enum HpeFocusedTable { TopList, ComponentList, ItemList, CuttingList }

export type HpeDetailsSelection = {
  costCenterId?: ID;
  componentId?: ID;
  itemId?: ID;
  detailsTabName?: string;
  focusedTable?: HpeFocusedTable;
  focusedCell?: FocusedCell;
  highlightInvalidFormula?: boolean;
};

export type HpeDetailsMasterInfo = {
  name: string;
  code: string;
  cost: number;
  total: number;
  masterZoneHouseEstimatePlansFolderId: ID;
};

export type HpeDetailsState = WithFormMode<WithRecalculationNeeded<WithDirty<{
  // if we come from quote page by clicking on "Sales Estimate" button
  // we have to recalculate HPE in the provided price book
  priceBookId?: ID;
  housePlanId: ID;
  masterInfo: HpeDetailsMasterInfo;
  hpComponentsCcId: ID;
  uoms: Table<HpeUomRefItem>;
  tops: Table<HpeTopRefItem>;
  hpTops: Table<HpeTop>;
  hpCostCenters: Table<HpeCostCenter>;
  hpComponents: Table<HpeComponent>;
  hpItems: Table<HpeItem>;
  selection: HpeDetailsSelection;
  isProcessChangesAsap: boolean;
  memo?: string;
  history?: HpeDetailsState[];
  historyPosition: number;
  historyLength: number;
}>>>;

export type HpeElementInfo = {
  type: HpeElementType;
  id: ID;
};

export const HPE_SALES_TAX_DIVIDER: HpeItem = {
  costCenterId: '',
  hpItemId: '_DIVIDER',
  itemId: '',
  code: '',
  name: '',
  isSalesTax: false,
  quantity: 0,
  cost: 0,
  uomId: '',
  extension: 0,
  isActive: true,
  isFormulaNeedPropagation: false,
  type: HpeItemType.Divider,
};

export const hpeTopComparator = valueComparator<HpeTop>((top) => top.code.toLowerCase());
export const hpeComponentComparator = composeComparators<HpeComponent>(
  newEntityComparator,
  valueComparator('code'),
  valueComparator(getHpeComponentId));
export const hpeItemComparator = composeComparators<HpeItem>(
  newEntityComparator,
  valueComparator('code'),
  valueComparator(getHpeItemId));
export const hpeCcComparator = composeComparators<HpeCostCenter>(
  valueComparator('code'),
  valueComparator(getHpeCostCenterId));

export const hpeUomRefTable = createTableDriver(getHpeUomRefItemId);
export const hpeTopRefTable = createTableDriver(getHpeTopRefItemId);
export const hpeTopTable = createTableDriver(
  getHpeTopId,
  createOrderIndexDriver('code', getHpeTopId, whenExisting, hpeTopComparator),
  createOrderIndexDriver(
    'errors',
    getHpeTopId,
    overEvery([isEntityInvalid, whenExisting]),
    hpeTopComparator));
export const hpeCcTable = createTableDriver(
  getHpeCostCenterId,
  createOrderIndexDriver('code', getHpeCostCenterId, whenExisting, hpeCcComparator));
export const hpeComponentTable = createTableDriver<HpeComponent>(
  getHpeComponentId,
  createOrderIndexDriver('code', getHpeComponentId, whenExisting, hpeComponentComparator),
  createOrderIndexDriver(
    'errors',
    getHpeComponentId,
    overEvery([isEntityInvalid, whenExisting]),
    hpeComponentComparator),
  createGroupArrayIndexDriver(
    'topId',
    getHpeComponentId,
    whenExisting,
    (component) => component.topIds,
    valueComparator(getHpeComponentId)));
export const hpeItemTable = createTableDriver(
  getHpeItemId,
  createGroupIndexDriver(
    'costCenter',
    getHpeItemId,
    whenExisting,
    (item) => item.costCenterId,
    hpeItemComparator),
  createGroupIndexDriver(
    'costCenterErrors',
    getHpeItemId,
    overEvery([isEntityInvalid, whenExisting]),
    (item) => item.costCenterId,
    hpeItemComparator),
  createGroupArrayIndexDriver(
    'topId',
    getHpeItemId,
    whenExisting,
    (item) => item.topIds,
    valueComparator<HpeItem>(getHpeItemId)));

export const hpeCuttingListComparator = composeComparators<HpeCuttingListItem>(
  newEntityComparator,
  valueComparator('sortOrder'),
  valueComparator(getHpeCuttingListItemId));

export const HPE_DETAILS_INIT_STATE: HpeDetailsState = {
  formMode: FormMode.CreateMode,
  housePlanId: '',
  masterInfo: { code: '', name: '', cost: 0, masterZoneHouseEstimatePlansFolderId: EMPTY_ID, total: 0 },
  hpComponentsCcId: '',
  uoms: hpeUomRefTable.create(),
  tops: hpeTopRefTable.create(),
  hpTops: hpeTopTable.create(),
  hpCostCenters: hpeCcTable.create(),
  hpComponents: hpeComponentTable.create(),
  hpItems: hpeItemTable.create(),
  selection: {},
  isDirty: false,
  isRecalculationNeeded: false,
  isProcessChangesAsap: false,
  history: [],
  historyPosition: 0,
  historyLength: 12,
};

export const queryHpeCost = (state: HpeDetailsState) => state.masterInfo.cost;
export const queryHpeHousePlanId = (state: HpeDetailsState) => state.housePlanId;
export const queryHpePriceBookId = (state: HpeDetailsState) => state.priceBookId;
export const queryHpeComponents = (state: HpeDetailsState) => hpeComponentTable.byOrder(state.hpComponents, 'code');
export const queryHpeItems = (state: HpeDetailsState) => hpeItemTable.all(state.hpItems);
export const queryHpeDirtyComponents = (state: HpeDetailsState) => hpeComponentTable.all(state.hpComponents)
  .filter(whenDirty);
export const queryHpeComponent = (state: HpeDetailsState, componentId: ID) =>
  hpeComponentTable.byId(state.hpComponents, componentId);
export const queryHpeFirstComponent = (state: HpeDetailsState) => first(queryHpeComponents(state));
export const queryHpeComponentItems = (state: HpeDetailsState, componentId: ID) =>
  get(hpeComponentTable.byId(state.hpComponents, componentId), 'items', []);
export const queryHpeCostCenterItems = (state: HpeDetailsState, costCenterId: ID) =>
  hpeItemTable.byGroup(state.hpItems, 'costCenter', costCenterId);
export const queryHpeDirtyCostCenters = (state: HpeDetailsState) =>
  hpeCcTable.all(state.hpCostCenters).filter(whenDirty);
export const queryHpeDirtyItems = (state: HpeDetailsState) => hpeItemTable.all(state.hpItems).filter(whenDirty);
export const queryHpeAllTops = (state: HpeDetailsState) => hpeTopTable.all(state.hpTops);
export const queryHpeDirtyCostCenterItems = (state: HpeDetailsState, costCenterId: ID) =>
  hpeItemTable.all(state.hpItems).filter((item) => item.costCenterId === costCenterId && whenDirty(item));
export const queryHpeItem = (state: HpeDetailsState, itemId: ID) => hpeItemTable.byId(state.hpItems, itemId);
export const queryHpeCostCenters = (state: HpeDetailsState) => hpeCcTable.byOrder(state.hpCostCenters, 'code');
export const queryHpeCostCenter = (state: HpeDetailsState, id: ID) => hpeCcTable.byId(state.hpCostCenters, id);
export const queryHpeCcc = (state: HpeDetailsState) =>
  <HpeCostCenter>hpeCcTable.byId(state.hpCostCenters, state.hpComponentsCcId);
export const queryHpeCccId = (state: HpeDetailsState) => state.hpComponentsCcId;
export const queryHpeIsCccId = (state: HpeDetailsState, id: ID) => state.hpComponentsCcId === id;

export const queryHpeHistory = (state: HpeDetailsState) => state.history ? state.history[state.historyPosition] : undefined;
export const queryHpeHistoryPosition = (state: HpeDetailsState) => state.historyPosition;
export const queryHpeUndoDisabled = (state: HpeDetailsState) => !state.history || !state.history.length ||
  state.history.length === 1 ||
  state.historyPosition === 0;
export const queryHpeRedoDisabled = (state: HpeDetailsState) =>  !state.history || !state.history.length ||
  state.historyPosition === state.history.length - 1;

export const queryHpeTops = (state: HpeDetailsState) => hpeTopTable.byOrder(state.hpTops, 'code');

export const queryHpeTopRefItem = (state: HpeDetailsState, id: ID) => hpeTopRefTable.byId(state.tops, id);
export const queryHpeTopRefItems = (state: HpeDetailsState) => hpeTopRefTable.all(state.tops);

export const queryHpeUomRefItem = (state: HpeDetailsState, id: ID) => hpeUomRefTable.byId(state.uoms, id);

export const queryHpeTop = (state: HpeDetailsState, id: ID) => hpeTopTable.byId(state.hpTops, id);

export const queryHpeEditable = (state: HpeDetailsState) => isEditableFormMode(queryFormMode(state));

export const queryHpeProcessChangesAsap = (state: HpeDetailsState) => state.isProcessChangesAsap;

export const queryHpeElement = (state: HpeDetailsState,
                                sourceType: HpeElementType,
                                sourceId: ID): HpeElement => {
  if (sourceType === HpeElementType.Component) {
    return <HpeComponent>hpeComponentTable.byId(state.hpComponents, sourceId);
  } else {
    return <HpeItem>hpeItemTable.byId(state.hpItems, sourceId);
  }
};

export const queryHpeCuttingList = (state: HpeDetailsState,
                                    sourceType: HpeElementType,
                                    sourceId: ID): HpeCuttingListItem[] =>
  get(queryHpeElement(state, <any>sourceType, sourceId), 'cuttingList') || [];

export const queryHpeSelectedElementInfo = (state: HpeDetailsState): Maybe<HpeElementInfo> => {
  const { componentId, itemId } = state.selection;

  if (componentId) {
    return { type: HpeElementType.Component, id: componentId };
  } else if (itemId) {
    return { type: HpeElementType.Item, id: itemId };
  } else {
    return void 0;
  }
};

export const queryHpeSelectedCostCenter = (state: HpeDetailsState): Maybe<HpeCostCenter> => {
  const { selection: { costCenterId } } = state;
  if (costCenterId) {
    return hpeCcTable.byId(state.hpCostCenters, costCenterId);
  } else {
    return void 0;
  }
};

export const queryHpeSelectedCostCenterId = (state: HpeDetailsState) => state.selection.costCenterId;

export const queryIsCccSelected = (state: HpeDetailsState) => {
  const ccId = queryHpeSelectedCostCenterId(state);
  return ccId ? queryHpeIsCccId(state, ccId) : false;
};

export const querySelectedComponent = (state: HpeDetailsState): Maybe<HpeComponent> => {
  const { selection: { componentId } } = state;
  if (componentId) {
    return hpeComponentTable.byId(state.hpComponents, componentId);
  } else {
    return void 0;
  }
};

export const queryHpeSelectedComponentId = (state: HpeDetailsState) => state.selection.componentId;

export const queryHpeSelectedItemId = (state: HpeDetailsState) => state.selection.itemId;

export const queryHpeSelectedItem = (state: HpeDetailsState): Maybe<HpeItem> => {
  const { selection: { itemId } } = state;
  if (itemId) {
    return hpeItemTable.byId(state.hpItems, itemId);
  } else {
    return void 0;
  }
};

export const queryHpeSelectedDetailsTab = (state: HpeDetailsState) => state.selection.detailsTabName;

export const queryHpeCostCenterItemCount = (state: HpeDetailsState, ccId: ID) =>
  hpeItemTable.byGroup(state.hpItems, 'costCenter', ccId).length;

export const queryHpeMasterInfo = (state: HpeDetailsState) => state.masterInfo;

export const ensureHpeSalesTaxDivider = (items: HpeItem[]): HpeItem[] => {
  if (find(items, { type: HpeItemType.SalesTax })) {
    return items.concat(HPE_SALES_TAX_DIVIDER);
  } else {
    return items;
  }
};

export const queryHpeHasErrors = (state: HpeDetailsState) => {
  if (!isEmpty(hpeTopTable.byOrder(state.hpTops, 'errors'))) {
    return true;
  }
  if (!isEmpty(hpeComponentTable.byOrder(state.hpComponents, 'errors'))) {
    return true;
  }
  return hpeCcTable.all(state.hpCostCenters).some((cc) => !isEmpty(hpeItemTable.byGroup(
    state.hpItems,
    'costCenterErrors',
    getHpeCostCenterId(cc))));
};

export const queryHpeSaveModel = (state: HpeDetailsState): HpeDetailsSave => {
  if (queryFormModeCreate(state)) {
    return {
      priceBookId: state.priceBookId,
      housePlanId: state.housePlanId,
      hpCostCenters: queryHpeCostCenters(state).map(setNewDirtyType),
      hpComponents: queryHpeComponents(state).map(setNewDirtyType),
      hpItems: queryHpeItems(state).filter(isHpeFreeItem).map(setNewDirtyType),
      hpTops: queryHpeTops(state),
    };
  } else {
    return {
      priceBookId: state.priceBookId,
      housePlanId: state.housePlanId,
      hpCostCenters: queryHpeDirtyCostCenters(state),
      hpComponents: queryHpeDirtyComponents(state),
      hpItems: queryHpeDirtyItems(state).filter(isHpeFreeItem),
      hpTops: queryHpeAllTops(state),
      memo: state.memo,
    };
  }
};

export const queryHpeTopViewUsage = (state: HpeDetailsState, topId: ID): HpeTopViewUsage => {
  const top = queryHpeTop(state, topId);
  const ccc = queryHpeCcc(state);

  const components = queryHpeComponentsByTopId(state, topId)
    .map((component) => ({
      costCenterId: getHpeCostCenterId(ccc),
      costCenterCode: ccc.code,
      recordId: getHpeComponentId(component),
      recordCode: component.code,
    }));

  const costCenters = keyBy(queryHpeCostCenters(state), getHpeCostCenterId);

  const items = queryHpeItemsByTopId(state, topId)
    .map((item) => ({
      costCenterId: item.costCenterId,
      costCenterCode: costCenters[item.costCenterId].code,
      recordId: getHpeItemId(item),
      recordCode: item.code,
    }));

  return {
    hpCode: state.masterInfo.code,
    hpName: state.masterInfo.name,
    topName: top && top.code || '',
    records: components.concat(sortBy(items, 'costCenterCode')),
  };
};

export const queryHpeCcHasErrors = (state: HpeDetailsState, ccId: ID) =>
  hpeItemTable.byGroup(state.hpItems, 'costCenterErrors', ccId).length > 0;

export const queryHpeFirstInvalidItem = (state: HpeDetailsState, ccId: ID) =>
  first(hpeItemTable.byGroup(state.hpItems, 'costCenterErrors', ccId));
export const queryHpeFirstInvalidComponent = (state: HpeDetailsState) =>
  first(hpeComponentTable.byOrder(state.hpComponents, 'errors'));
export const queryHpeFirstInvalidTop = (state: HpeDetailsState) =>
  first(hpeTopTable.byOrder(state.hpTops, 'errors'));

export const queryHpeFocusedCell = ({ selection: { focusedTable, focusedCell } }: HpeDetailsState,
                                    table: HpeFocusedTable) => focusedTable === table ? focusedCell : void 0;

export const queryHpeComponentsByTopId = (state: HpeDetailsState, topId: ID) =>
  hpeComponentTable.byGroup(state.hpComponents, 'topId', topId);

export const queryHpeItemsByTopId = (state: HpeDetailsState, topId: ID) =>
  hpeItemTable.byGroup(state.hpItems, 'topId', topId);

export const queryHpeIsSelectionEmpty = (state: HpeDetailsState) => isEmpty(state.selection);

export const queryHpeGetCostCenterRequest = (state: HpeDetailsState,
                                             costCenterId: ID): HpeDetailsGetCostCenterRequest => {
  if (queryFormModeCreate(state)) {
    return {
      priceBookId: state.priceBookId,
      housePlanId: queryHpeHousePlanId(state),
      costCenterId,
      costCenters: queryHpeCostCenters(state).map(setNewDirtyType),
      components: queryHpeComponents(state).map(setNewDirtyType),
      items: queryHpeItems(state).filter(isHpeFreeItem).map(setNewDirtyType),
      tops: queryHpeTops(state),
    };
  } else {
    return {
      priceBookId: state.priceBookId,
      housePlanId: queryHpeHousePlanId(state),
      costCenterId,
      costCenters: queryHpeDirtyCostCenters(state),
      components: queryHpeDirtyComponents(state),
      items: queryHpeDirtyItems(state).filter(isHpeFreeItem),
      tops: queryHpeTops(state),
    };
  }
};

export const queryHpeSelectedElement = (state: HpeDetailsState) => {
  const elementInfo = queryHpeSelectedElementInfo(state);
  return isNil(elementInfo) ? void 0 : queryHpeElement(state, elementInfo.type, elementInfo.id);
};

/**
 * Undo/Redo functionality
 */
export function initHistory(state: HpeDetailsState): HpeDetailsState {
  const newHistory = {
    ...omit(state, 'history')
  } as HpeDetailsState;
  return setEntityFields(state, {
    historyPosition: 0,
    history: [newHistory],
  });
}

export function saveViewToHistory(state: HpeDetailsState): HpeDetailsState {
  const history = state.history ? [...state.history] : [];
  const historyPosition = state.historyPosition;
  const currentHistory = {
    ...omit(state, 'history'),
  } as HpeDetailsState;
  history[historyPosition] = currentHistory;
  return setEntityFields(state, {
    history,
  });
}

export function addToHistory(state: HpeDetailsState): HpeDetailsState {
  const history = state.history ? state.history.slice(0, state.historyPosition + 1) : [];
  const newHistory = {
    ...omit(state, 'history'),
  } as HpeDetailsState;
  if (history.length < state.historyLength) {
    history.push(newHistory);
  } else {
    history.shift();
    history.push(newHistory);
  }
  const historyPosition = history.length - 1;
  return setEntityFields(state, {
    historyPosition,
    history,
  });
}

export function undo(state: HpeDetailsState): HpeDetailsState {
  const history = state.history ? [...state.history] : [];
  const historyPosition = state.historyPosition - 1;
  const undoState = history[historyPosition];
  return setEntityFields({...undoState}, {
    historyPosition,
    history,
    isDirty: undoState.isDirty,
  });
}

export function redo(state: HpeDetailsState): HpeDetailsState {
  const history = state.history ? [...state.history] : [];
  const historyPosition = state.historyPosition + 1;
  const redoState = history[historyPosition];
  return setEntityFields({...redoState}, {
    historyPosition,
    history,
    isDirty: redoState.isDirty,
  });
}

/**
 * Validates if currently selected element has error in the formula
 */
export const queryHpeElementHasErrors = (state: HpeDetailsState) => {
  const element = queryHpeSelectedElement(state);

  return element && isHpeElementHasInvalidFormula(element);
};

/**
 * Answers whether HPE has at least one ToP measured in GTO.
 */
export const queryHpeHasTopsFromGto = (state: HpeDetailsState) => queryHpeTops(state).some((top) => top.isFromGto);

export const queryHpeSelectionHighlightInvalid = ({ selection: { highlightInvalidFormula } }: HpeDetailsState) =>
  highlightInvalidFormula || false;

export const queryHpeCostCenterHasDirtyChanges = (state: HpeDetailsState, costCenterId: ID) =>
  queryHpeCostCenterItems(state, costCenterId).some(whenModified);

export const queryHpeElementFormulaCalculation = (state: HpeDetailsState, element: HpeElement) => {
  const { formula, uomId } = element;
  if (isNil(formula) || isFormulaEmpty(formula)) {
    return;
  }
  // tslint:disable-next-line:no-non-null-assertion
  const tops = formula.takeOffParams.map((topId) => queryHpeTop(state, topId)!);
  return {
    formula,
    uomId,
    takeOffParameters: tops.map(({ topId, value }) => ({ id: topId, value: UomQuantityMapper.serialize(value) })),
    isApproximated: tops.some(isHpeContractPriceTop),
  };
};

export const queryHpeComponentFormulaCalculation = (state: HpeDetailsState, componentId: ID) => {
  const component = queryHpeComponent(state, componentId);
  return isNil(component) ? component : queryHpeElementFormulaCalculation(state, component);
};

export const queryHpeItemFormulaCalculation = (state: HpeDetailsState, itemId: ID) => {
  const item = queryHpeItem(state, itemId);
  return isNil(item) ? item : queryHpeElementFormulaCalculation(state, item);
};

export const queryHpeFormulaHasTopsFromGto = (state: HpeDetailsState, formula: Formula) =>
  formula.takeOffParams.map((topId) => queryHpeTop(state, topId)).some((top) => top ? top.isFromGto : false)
  || formula.takeOffParams.map((topId) => queryHpeTopRefItem(state, topId)).some((top) => top ? !top.isCommon : false);
export const queryHpeFormulaFirstInvalidTop = (state: HpeDetailsState, formula: Formula) =>
  find(formula.takeOffParams.map((topId) => queryHpeTop(state, topId)), (top) => top ? isEntityInvalid(top) : false);

export const getHpeBestComponentDetailsTab = (component?: HpeComponent) => {
  if (isNil(component)) {
    return void 0;
  }

  return HpeComponentDetailsTab.Items;
};

export const queryHpeBestComponentDetailsTab = (state: HpeDetailsState, componentId?: ID) =>
  getHpeBestComponentDetailsTab(componentId ? queryHpeComponent(state, componentId) : void 0);

export const queryHpeBestItemDetailsTab = (state: HpeDetailsState, itemId?: ID) => {
  const item = itemId ? queryHpeItem(state, itemId) : void 0;
  if (isNil(item)) {
    return void 0;
  }
  if (isHpeItemFromComponent(item)) {
    return HpeItemDetailsTab.Component;
  } else if (!isFormulaEmpty(item.formula)) {
    return HpeItemDetailsTab.Formula;
  } else if (!isEmpty(item.cuttingList)) {
    return HpeItemDetailsTab.CuttingList;
  } else if (!isEmpty(item.memo)) {
    return HpeItemDetailsTab.Memo;
  }

  return HpeItemDetailsTab.CuttingList;
};
